import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, Reveal } from "../components/Motion";
import { INQUIRY_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", subject: "", message: "" };

const Field = ({ id, label, textarea, ...props }) => {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label htmlFor={id} className="group block border-b border-[var(--ink)]/30 focus-within:border-[var(--ink)] transition-colors duration-300">
      <span className="eyebrow block pt-6 text-[var(--ink-soft)]">{label}</span>
      <Comp
        id={id}
        className={`w-full bg-transparent py-3 text-base font-normal outline-none placeholder:text-[var(--ink-soft)]/60 ${textarea ? "min-h-[140px] resize-none" : ""}`}
        data-testid={`contact-form-${id}-input`}
        {...props}
      />
    </label>
  );
};

export default function Contact() {
  const [params] = useSearchParams();
  const initial = INQUIRY_TYPES.some((t) => t.id === params.get("type")) ? params.get("type") : "general";
  const [type, setType] = useState(initial);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, inquiry_type: type });
      setSent(true);
      setForm(EMPTY);
      toast.success("Thank you. Your message has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg ?? "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact 1818 Brands — Suppliers, Buyers & Partners"
        description="Contact 1818 Brands about available inventory, off-price retail supply or business partnerships. Send us a message and our team will respond."
      />
      <PageHeader
        eyebrow="Contact"
        title="Tell us what you have, or what you need."
        intro="Suppliers, retail buyers and potential partners can reach us using the form below."
        testId="contact-header"
      />
      <section className="container-x section">
        <div className="grid gap-16 lg:grid-cols-12">
          <aside className="lg:col-span-4 space-y-10" data-testid="contact-details">
            <Reveal>
              <p className="eyebrow mb-4">Company</p>
              <p className="text-xl font-normal tracking-[-0.01em]">1818 Brands LLC</p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="eyebrow mb-4">Office</p>
              <p className="text-[15px] text-[var(--ink-soft)]">[Office address to be provided]</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="eyebrow mb-4">Email</p>
              <p className="text-[15px] text-[var(--ink-soft)]">[Email to be provided]</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="eyebrow mb-4">Phone</p>
              <p className="text-[15px] text-[var(--ink-soft)]">[Phone to be provided]</p>
            </Reveal>
          </aside>
          <Reveal className="lg:col-span-7 lg:col-start-6 lg:pl-8">
            <form onSubmit={submit} className="relative" data-testid="contact-form">
              <p className="eyebrow mb-5">Inquiry type</p>
              <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Inquiry type" data-testid="contact-form-inquiry-type-select">
                {INQUIRY_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    role="radio"
                    aria-checked={type === t.id}
                    onClick={() => setType(t.id)}
                    className={`rounded-full border px-5 py-2.5 text-sm transition-[background-color,color,border-color] duration-300 ${
                      type === t.id ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-[var(--ink)]/30 hover:border-[var(--ink)]"
                    }`}
                    data-testid={`inquiry-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="mt-6 grid gap-x-10 sm:grid-cols-2">
                <Field id="name" label="Name" required value={form.name} onChange={set("name")} autoComplete="name" />
                <Field id="company" label="Company" required value={form.company} onChange={set("company")} autoComplete="organization" />
                <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
                <Field id="subject" label="Subject" required value={form.subject} onChange={set("subject")} />
              </div>
              <Field id="message" label="Message" textarea required value={form.message} onChange={set("message")} />
              <div className="mt-10 flex flex-wrap items-center justify-between gap-6">
                <p className="max-w-xs text-xs leading-relaxed text-[var(--ink-soft)]">
                  {sent ? <span data-testid="contact-form-success">Message received. We will be in touch.</span> : "All fields are required."}
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  className="group inline-flex h-12 items-center gap-3 rounded-full bg-[var(--ink)] px-8 text-sm font-medium text-[var(--paper)] transition-[transform,opacity] duration-500 hover:-translate-y-0.5 disabled:opacity-60"
                  data-testid="contact-form-submit-button"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send message
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
